import { formatDate, formatMoney, isOverdue, plural } from '@/lib/format'
import type { WeddingData } from '@/mock/types'
import { type BudgetFilter, budgetTotals, expenseTitle } from './model'

export type BudgetPromptAction =
  | { kind: 'paid'; expenseId: string; paymentId: string }
  | { kind: 'open'; expenseId: string; label: string; tab?: 'payments' }
  | { kind: 'filter'; filter: BudgetFilter; label: string }
  | { kind: 'add-supplier'; supplierId: string }
  | { kind: 'total' }

export type BudgetPrompt = { code: string; title: string; hint: string; action: BudgetPromptAction }

/**
 * "Na co nezapomenout" for Rozpočet, from the budget itself: overdue payments,
 * a plan over the total, items without a price or a payment plan, agreed
 * suppliers missing from the budget, estimates and the reserve.
 */
export function budgetPrompts(data: WeddingData): BudgetPrompt[] {
  const { expenses } = data.budget
  const totals = budgetTotals(data)
  const title = (id: string) => {
    const expense = expenses.find((item) => item.id === id)
    return expense ? expenseTitle(expense, data) : ''
  }

  const overdue = expenses.flatMap((expense) =>
    expense.payments
      .filter((payment) => !payment.paid && isOverdue(payment.dueDate))
      .map(
        (payment): BudgetPrompt => ({
          code: `budget-overdue-${payment.id}`,
          title: `Odešla platba pro ${title(expense.id)}?`,
          hint: `${payment.name} ${formatMoney(payment.amount)}, splatnost ${formatDate(payment.dueDate ?? '', 'long')}.`,
          action: { kind: 'paid', expenseId: expense.id, paymentId: payment.id },
        }),
      ),
  )

  const unpriced = expenses.filter((expense) => expense.amount === 0)
  const unplanned = expenses.filter(
    (expense) => !expense.estimate && expense.amount > 0 && expense.payments.length === 0,
  )
  const missing = data.suppliers.filter(
    (supplier) =>
      (supplier.status === 'agreed' || supplier.status === 'booked') &&
      !expenses.some((expense) => expense.supplierId === supplier.id),
  )
  const reserveLow =
    totals.total !== null &&
    totals.unallocated !== null &&
    totals.unallocated >= 0 &&
    totals.unallocated < totals.total * 0.05

  const rules: (BudgetPrompt | false)[] = [
    ...overdue,
    totals.unallocated !== null &&
      totals.unallocated < 0 && {
        code: 'budget-over',
        title: 'Jak dorovnáte rozpočet?',
        hint: `Naplánováno je o ${formatMoney(-totals.unallocated)} víc, než kolik máte k dispozici.`,
        action: { kind: 'total' },
      },
    ...unpriced.map(
      (expense): BudgetPrompt => ({
        code: `budget-unpriced-${expense.id}`,
        title: `Kolik bude stát ${expenseTitle(expense, data)}?`,
        hint: 'Položka zatím nemá cenu, takže v součtu chybí.',
        action: { kind: 'open', expenseId: expense.id, label: 'Doplnit cenu' },
      }),
    ),
    ...missing.map(
      (supplier): BudgetPrompt => ({
        code: `budget-missing-${supplier.id}`,
        title: `Kolik stojí ${supplier.name}?`,
        hint: 'Dodavatel je domluvený, ale v rozpočtu zatím chybí.',
        action: { kind: 'add-supplier', supplierId: supplier.id },
      }),
    ),
    ...unplanned.map(
      (expense): BudgetPrompt => ({
        code: `budget-unplanned-${expense.id}`,
        title: `Kdy se platí ${expenseTitle(expense, data)}?`,
        hint: `Cena ${formatMoney(expense.amount)} je dohodnutá, ale žádná platba zatím není naplánovaná.`,
        action: {
          kind: 'open',
          expenseId: expense.id,
          label: 'Naplánovat platby',
          tab: 'payments',
        },
      }),
    ),
    totals.estimates > 0 && {
      code: 'budget-estimates',
      title: 'Které ceny jsou zatím jen odhad?',
      hint: `${totals.estimates} ${plural(totals.estimates, 'položka', 'položky', 'položek')} za ${formatMoney(totals.estimated)} čeká na nabídku nebo smlouvu.`,
      action: { kind: 'filter', filter: 'estimate', label: 'Zobrazit odhady' },
    },
    reserveLow && {
      code: 'budget-reserve',
      title: 'Máte rezervu na nečekané výdaje?',
      hint: `Volných zbývá ${formatMoney(totals.unallocated ?? 0)}. Obvykle se nechává 5–10 % rozpočtu.`,
      action: { kind: 'total' },
    },
  ]

  const hidden = new Set(data.hiddenPrompts)
  return rules.filter((rule): rule is BudgetPrompt => rule !== false && !hidden.has(rule.code))
}
